import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import AddNoReplyNeeded from 'actions/conversation_item/no_reply_needed/add_no_reply_needed';
import connect from 'components/lib/connect';
import ConversationItem from 'models/conversation_item';
import ConversationItemMenu from './conversation_item_menu';
import ConversationMessage, { MessageChannelType } from 'models/conversation_message';
import CopyItemLink from 'actions/composition/copy_item_link';
import ChatMessage from 'models/chat_message';
import EditTaskItem from 'actions/composition/edit_task_item';
import EmailMessage from 'models/email_message';
import FbMessageIncoming from 'models/fb_message_incoming';
import FollowTask from 'actions/task_follower/follow_task';
import ForwardEmail from 'actions/composition/forward_email';
import ItemsContext from 'components/customer/conversation_history/conversation_items_v2/items_context';
import OpenOrRefreshCommentPanel from 'actions/task/open_or_refresh_comment_panel';
import PhoneCall from 'models/phone_call';
import RecordingStatus from 'models/recording_status';
import ReplyToEmail from 'actions/composition/reply_to_email';
import ReportPoorCallQuality from 'actions/conversation_item/phone_call/report_poor_call_quality';
import ShowDeleteVoiceRecordingModal from 'actions/conversation/show_delete_voice_recording_modal';
import SMSMessage from 'models/sms_message';
import TaskAnalyticsLocations from 'actions/conversation_item/task/task_analytics_locations';
import Task from 'models/task';
import TrackItemMenuOpened from 'actions/conversation_item/task/track_item_menu_opened';
import { typeReflect } from 'models/conversation_item/content_from_js';
import UnfollowTask from 'actions/task_follower/unfollow_task';
import Voicemail from 'models/voicemail';

function mapStateToProps(context, { customerId, currentAgentId, item, isNoReplyNeededVisible }) {
  const isEmailItem = item.content instanceof EmailMessage;
  const canManageVoiceRecordings = canManageRecordings(context, item);
  const isLinkItem = item.isLink();
  const isTask = item.content instanceof Task;
  let isFollowVisible = false;
  let isUnfollowVisible = false;
  const taskInfoProvider = context.getProvider('taskInfo');

  if (isTask) {
    let taskInfo = taskInfoProvider.findBy({ id: item.id });
    if (!taskInfo || !_.some(taskInfo.getFollowers(), f => f.agentId === currentAgentId)) {
      isFollowVisible = true;
    } else {
      isUnfollowVisible = true;
    }
  }
  const canReportPoorCallQuality = canAgentReportPoorCallQuality(context, item);

  return {
    contentType: item.contentType(),
    isMenuVisible: !isLinkItem,
    conversationItemId: item.id,
    isNoReplyNeededVisible,
    isForwardVisible: isEmailItem,
    isReactionVisible: canReact(item),
    isReplyVisible: isEmailItem,
    isFollowVisible,
    isUnfollowVisible,
    canManageVoiceRecordings,
    canReportPoorCallQuality,
    recordingUrl: canManageVoiceRecordings ? getRecordingUrl(context, item) : null,
  };
}

function canAgentReportPoorCallQuality(context, item) {
  if (item.content instanceof PhoneCall) {
    const currentAgent = context.getProvider('currentAgent').get();
    const hasCallId = !!item.content.callId;
    const isAgentParticipant = currentAgent && !!item.content.findParticipantById(currentAgent.id);
    return hasCallId && isAgentParticipant;
  }

  return false;
}

function canManageRecordings(context, item) {
  return (
    context.isFeatureEnabled('manageVoiceRecordings') &&
    ((item.content instanceof PhoneCall && !item.content.isAbandonedCall()) || item.content instanceof Voicemail) &&
    item.content.recordingStatus === RecordingStatus.AVAILABLE
  );
}

function getRecordingUrl(context, item) {
  let orgId = context
    .getProvider('auth')
    .get()
    .getOrgId();
  let baseUrl = `${window.location.origin}/api/v1/orgs/${orgId}/voice`;
  let recordingUrl;
  if (item.content instanceof PhoneCall) {
    recordingUrl = `${baseUrl}/call-recordings/${item.content.recordingId}`;
  } else {
    recordingUrl = `${baseUrl}/voicemail-recordings/${item.content.recordingId}`;
  }
  return recordingUrl;
}
function canReact(item) {
  const supportedChannels = [MessageChannelType.INSTAGRAM_DIRECT];
  // The reaction menu item should be present for Customer items of Conversation Item
  // type with a channel of Instagram Direct (more in the future) and that do not already
  // have a reaction
  if (
    item.isCustomerItem() &&
    item.content instanceof ConversationMessage &&
    supportedChannels.includes(item.content.channel) &&
    !item.content.hasReaction()
  ) {
    return true;
  }
  return false;
}

function mapExecuteToProps(executeAction, props) {
  return {
    onForward: () => executeAction(ForwardEmail, { item: props.item }),
    onReply: () => executeAction(ReplyToEmail, { item: props.item }),
    onReaction: _.noop(), // TODO wire up reaction menu
    onNoReplyNeeded: () => executeAction(AddNoReplyNeeded, {}),
    onEditTask: () => executeAction(EditTaskItem, { item: props.item }),
    onDeleteRecording: () => {
      executeAction(ShowDeleteVoiceRecordingModal, {
        customerId: props.customerId,
        conversationItemId: props.item.id,
        isCallRecording: props.item.content instanceof PhoneCall,
      });
    },
    onOpenMenu: () => {
      const itemType = typeReflect.instanceToType(props.item.content);
      executeAction(TrackItemMenuOpened, {
        itemId: props.item.id,
        itemType,
        location: props.location || TaskAnalyticsLocations.TASK_ITEM,
      });
    },
    onReportPoorCallQuality: () => {
      executeAction(ReportPoorCallQuality, {
        customerId: props.customerId,
        conversationItem: props.item,
      });
    },
    onCopyLink: () => executeAction(CopyItemLink, { item: props.item }),
    onFollowTask: () => executeAction(FollowTask, { itemId: props.item.id }),
    onUnfollowTask: () => executeAction(UnfollowTask, { itemId: props.item.id }),
    openSidePanel: () =>
      executeAction(OpenOrRefreshCommentPanel, {
        itemId: props.item.id,
        location: TaskAnalyticsLocations.CONVERSATION_ITEM_MENU,
      }),
  };
}

export const ConversationItemMenuContainer = connect(mapStateToProps, mapExecuteToProps)(ConversationItemMenu);
ConversationItemMenuContainer.propTypes = {
  customerId: PropTypes.string.isRequired,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  isNoReplyNeededVisible: PropTypes.bool,
};

function mapWrappedStateToProps({ getProvider, isFeatureEnabled }, props) {
  let currentAgent = getProvider('currentAgent').get();
  return {
    customerId: getProvider('currentLocation').get().customerId,
    currentAgentId: _.get(currentAgent, 'id'),
    isNoReplyNeededVisible: isNoReplyNeededVisible(),
    ...props,
  };

  function isNoReplyNeededVisible() {
    const { lastCustomerItemId, latestConversation, item } = props;
    if (item.id === lastCustomerItemId && isRealtimeMessage(item)) {
      return true;
    }

    if (hasSla(latestConversation)) {
      if (_(latestConversation).get('sla.setByConversationItemId') === item.id || item.id === lastCustomerItemId) {
        return true;
      }
    }

    return false;
  }

  function isRealtimeMessage(item) {
    return (
      item.content instanceof ChatMessage ||
      item.content instanceof SMSMessage ||
      item.content instanceof ConversationMessage ||
      item.content instanceof FbMessageIncoming
    );
  }

  function hasSla(conversation) {
    return !!_(conversation).get('sla.dueAt');
  }
}

const WrappedConversationItemMenuContainer = connect(mapWrappedStateToProps)(ConversationItemMenuContainer);
ConversationItemMenuContainer.propTypes = {
  item: PropTypes.instanceOf(ConversationItem).isRequired,
};

export { WrappedConversationItemMenuContainer };

export default function ItemMenuContainer(props) {
  return (
    <ItemsContext.Consumer>
      {({ lastCustomerItemId, latestConversation }) => (
        <WrappedConversationItemMenuContainer
          lastCustomerItemId={lastCustomerItemId}
          latestConversation={latestConversation}
          {...props}
        />
      )}
    </ItemsContext.Consumer>
  );
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/conversation_history/conversation_items_v2/menu/conversation_item_menu_container.js